import { useEffect, useRef, useState } from "react";

/**
 * The prototype's chip multi-select — a trigger showing what is chosen, and a searchable
 * checkbox panel.
 *
 * Used for the accredited sectors, where the list is forty entries long and a body picks
 * three. A checkbox grid would put the whole IAF taxonomy on screen to record a handful of
 * selections; the chips keep what was actually chosen visible, which is the thing being
 * reviewed later.
 *
 * The search field is a plain `type="text"` like every other input here.
 *
 * Bulk selection goes through `onSetAll`, which takes the whole next selection, rather than
 * through a loop of `onToggle`. A loop is only correct where the parent updates state
 * functionally; where the handler closes over the current draft — as the intake form's does
 * — forty calls in one event all read the same stale array and thirty-nine are discarded.
 * `onSetAll` is required rather than optional so a multi-select added later cannot quietly
 * ship without the control.
 */

export interface MultiSelectOption {
  value: number;
  label: string;
}

export function MultiSelect({
  id,
  options,
  selected,
  onToggle,
  onSetAll,
  placeholder = "Select…",
  invalid = false,
  searchLabel = "Filter",
  noun = "sector",
}: {
  id: string;
  options: readonly MultiSelectOption[];
  selected: readonly number[];
  onToggle: (value: number) => void;
  onSetAll: (values: number[]) => void;
  placeholder?: string;
  invalid?: boolean;
  searchLabel?: string;
  noun?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const container = useRef<HTMLDivElement>(null);

  // Clicking away closes the panel, as a native select would.
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? options.filter(
        (option) =>
          option.label.toLowerCase().includes(needle) || String(option.value) === needle,
      )
    : options;

  // Bulk actions apply to what the panel is showing, so "select all" under a filter means
  // the matches and not the whole taxonomy — the alternative silently adds forty entries
  // when the auditor typed a filter precisely to narrow the list.
  const shownValues = shown.map((option) => option.value);
  const chosenShown = shownValues.filter((value) => selected.includes(value)).length;

  const selectShown = () => {
    const next = [...selected];
    for (const value of shownValues) if (!next.includes(value)) next.push(value);
    onSetAll(next);
  };
  const clearShown = () => onSetAll(selected.filter((value) => !shownValues.includes(value)));

  return (
    <div className={`msd${invalid ? " invalid" : ""}`} ref={container}>
      {/* A focusable div rather than a `<button>`, which is also what the prototype uses.
          Each chip carries its own remove button, and a button inside a button is invalid
          HTML that browsers reflow unpredictably — the inner control ends up outside the
          outer one, where a click on it does not do what it looks like it does. */}
      <div
        role="button"
        tabIndex={0}
        className="msd-trigger"
        id={id}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setOpen((current) => !current);
        }}
      >
        <span className="msd-chips">
          {selected.length === 0 ? <span className="msd-ph">{placeholder}</span> : null}
          {selected.map((value) => (
            <span className="msd-chip" key={value}>
              IAF {value}
              <button
                type="button"
                className="msd-x"
                title="Remove"
                aria-label={`Remove IAF ${value}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle(value);
                }}
              >
                ×
              </button>
            </span>
          ))}
        </span>
        <svg className="msd-caret" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>

      {open ? (
        <div className="msd-panel" role="listbox" aria-multiselectable="true">
          <div className="msd-search">
            <input
              type="text"
              value={query}
              placeholder={searchLabel}
              aria-label={searchLabel}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="msd-bulk">
            <button
              type="button"
              className="msd-bulk-act"
              disabled={shown.length === 0 || chosenShown === shown.length}
              onClick={selectShown}
            >
              {needle ? `Select all ${shown.length} matching` : `Select all ${options.length}`}
            </button>
            <button
              type="button"
              className="msd-bulk-act"
              disabled={chosenShown === 0}
              onClick={clearShown}
            >
              {needle ? "Clear matching" : "Clear all"}
            </button>
            <span className="msd-bulk-count" aria-live="polite">
              {selected.length} selected
            </span>
          </div>
          <div className="msd-list">
            {shown.map((option) => {
              const on = selected.includes(option.value);
              return (
                <label className="msd-opt" key={option.value}>
                  <input
                    type="checkbox"
                    className="visually-hidden"
                    checked={on}
                    onChange={() => onToggle(option.value)}
                  />
                  <span className={`msd-check${on ? " on" : ""}`} aria-hidden="true">
                    {on ? "✓" : ""}
                  </span>
                  <span className="msd-num">{option.value}</span>
                  <span>{option.label}</span>
                </label>
              );
            })}
            {shown.length === 0 ? <p className="msd-opt">No {noun} matches.</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
